'use client'

import { useSearchParams } from 'next/navigation'
import { useCallback, useMemo } from 'react'
import {
    PRODUCT_CATEGORY_OPTIONS,
    PRODUCT_CHARACTERISTIC_OPTIONS,
    PRODUCT_COLOR_OPTIONS,
    PRODUCT_FEATURE_OPTIONS,
    PRODUCT_MATERIAL_OPTIONS,
    PRODUCT_PRICE_RANGES,
    PRODUCT_SIZE_OPTIONS,
} from '@/constants/catalog'
import { useURLState } from '@/hooks/shared/useURLState'
import {
    type IProductFilters,
    type IProductListFilterKey,
    type IProductSortField,
    type IProductSortOrder,
} from '@/schemas/catalog/productFilters'
import {
    buildProductListTerm,
    EMPTY_PRODUCT_FILTERS,
    hasActiveFilters,
    parseProductFilters,
    productFiltersToURL,
} from '@/services/domain/catalog/ProductFiltersService'

const OPTIONS = {
    categories: PRODUCT_CATEGORY_OPTIONS,
    sizes: PRODUCT_SIZE_OPTIONS,
    colors: PRODUCT_COLOR_OPTIONS,
    features: PRODUCT_FEATURE_OPTIONS,
    materials: PRODUCT_MATERIAL_OPTIONS,
    characteristics: PRODUCT_CHARACTERISTIC_OPTIONS,
    priceRanges: PRODUCT_PRICE_RANGES,
} as const

/**
 * Filtros da listagem `/produtos`, com a URL como fonte da verdade (links e voltar do navegador funcionam). Cada
 * alteração grava a URL com `push` (como a loja faz hoje) e o `useProducts` reage aos filtros novos. As opções de
 * filtro são as listas fixas de `constants/catalog.ts` (a API não devolve opções).
 */
export function useProductFilters() {
    const searchParams = useSearchParams()
    const { updateURL } = useURLState()

    const query = searchParams?.toString() ?? ''
    const filters = useMemo(() => parseProductFilters(new URLSearchParams(query)), [query])

    const setFilters = useCallback(
        (next: IProductFilters) => updateURL(productFiltersToURL(next), { replace: false, scroll: false }),
        [updateURL],
    )

    const toggleFilterValue = useCallback(
        (key: IProductListFilterKey, value: string) => {
            const current = filters[key]
            const next = current.includes(value) ? current.filter((item) => item !== value) : [...current, value]
            setFilters({ ...filters, [key]: next })
        },
        [filters, setFilters],
    )

    const setFilterValues = useCallback(
        (key: IProductListFilterKey, values: string[]) => setFilters({ ...filters, [key]: values }),
        [filters, setFilters],
    )

    const setTerm = useCallback((term: string) => setFilters({ ...filters, term: term.trim() }), [filters, setFilters])

    const setPriceRange = useCallback(
        (priceFrom: number | null, priceTo: number | null) => setFilters({ ...filters, priceFrom, priceTo }),
        [filters, setFilters],
    )

    /** `null` tira a ordenação. Sem `orderBy`, a API ordena por preço. */
    const setOrder = useCallback(
        (order: IProductSortOrder | null, orderBy: IProductSortField | null = null) =>
            setFilters({ ...filters, order, orderBy: order ? orderBy : null }),
        [filters, setFilters],
    )

    /** Limpa os filtros e mantém o termo buscado (comportamento atual da loja). */
    const resetFilters = useCallback(
        () => setFilters({ ...EMPTY_PRODUCT_FILTERS, term: filters.term }),
        [filters.term, setFilters],
    )

    return {
        filters,
        /** `term` no formato da API, para depuração e para quem chamar o service direto. */
        apiTerm: buildProductListTerm(filters),
        hasActiveFilters: hasActiveFilters(filters),
        options: OPTIONS,
        setFilters,
        toggleFilterValue,
        setFilterValues,
        setTerm,
        setPriceRange,
        setOrder,
        resetFilters,
    }
}
